import Konva from "konva";
import { getVideoUrl } from "../../../util/videoUrlHelper";
import { BASE_URL } from "../../../config";
import { formatDateToAppTimezoneAlt } from "../../../util/timezoneUtils";

export const getSelectedBtnProps = (isSelected) =>
  isSelected
    ? {
        colorScheme: "blue",
        variant: "solid",
      }
    : { color: "#444" };

export function applyTransparency(color, opacity = 1) {
  const clampedOpacity = Math.min(Math.max(opacity, 0), 1);

  if (!color) return `rgba(0, 0, 0, ${clampedOpacity})`;

  // Case: rgba or rgb string
  if (color.startsWith("rgba")) {
    return color.replace(
      /rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/,
      `rgba($1, $2, $3, ${clampedOpacity})`
    );
  }

  if (color.startsWith("rgb")) {
    return color.replace(
      /rgb\((\d+),\s*(\d+),\s*(\d+)\)/,
      `rgba($1, $2, $3, ${clampedOpacity})`
    );
  }

  // Case: Hex
  if (color.startsWith("#")) {
    let hex = color;

    // Expand shorthand hex like #abc → #aabbcc
    if (hex.length === 4) {
      hex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
  }

  // Fallback
  return color;
}

export const convertImageToBase64 = (img) => {
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);

  return canvas.toDataURL("image/png"); // or "image/jpeg"
};

// Add this util function to persist canvasPages in localStorage
// Stores only lightweight metadata and explicitly strips heavy fields like 'snapshot' and 'elements'
export const persistCanvasPages = (pages) => {
  try {
    const lightweightPages = (Array.isArray(pages) ? pages : [])
      .map((p) => ({
        id: p?.id || p?.pageId,
        zoomLevel: typeof p?.zoomLevel === "number" ? p.zoomLevel : 100,
        lastModified: p?.lastModified || new Date().toISOString(),
        canvasHeight: p?.canvasHeight,
        newlyAdded: p?.newlyAdded,
        // Do NOT include snapshot - base64 PNG causes QuotaExceededError; snapshots live in IndexedDB only
        text_boxes: Array.isArray(p?.text_boxes) ? p.text_boxes : [],
        text_box_content_data: Array.isArray(p?.text_box_content_data)
          ? p.text_box_content_data
          : [],
        // elements are intentionally omitted to avoid localStorage bloat
      }))
      .filter((p) => p.id);

    localStorage.setItem("canvasPages", JSON.stringify(lightweightPages));
  } catch (err) {
  }
};

export function measureHtmlHeight(html, width, fontSize, fontFamily) {
  const container = document.createElement("div");

  // ✅ Ensure the first line is not ignored if it's just a break or empty
  const htmlWithFirstLineFix =
    html.trim().startsWith("<br") || html.trim().startsWith("<div><br")
      ? "&nbsp;" + html
      : html;

  // ✅ Remove only positioning styles that affect height calculation
  const cleanedHtml = html.replace(/style="[^"]*"/g, (match) => {
    return match
      .replace(/position:\s*[^;]+;?/gi, "")
      .replace(/transform:[^;]+;?/gi, "")
      .replace(/top:\s*[^;]+;?/gi, "")
      .replace(/left:\s*[^;]+;?/gi, "")
      .replace(/z-index:\s*[^;]+;?/gi, "")
      .replace(/style=""/g, "");
  });

  container.innerHTML = cleanedHtml;

  container.style.position = "absolute";
  container.style.visibility = "hidden";
  container.style.pointerEvents = "none";
  container.style.width = `${width}px`;
  container.style.fontSize = `${fontSize}px`;
  container.style.fontFamily = fontFamily;
  container.style.lineHeight = "1.4";
  container.style.whiteSpace = "pre-wrap";
  container.style.wordBreak = "break-word";

  // ✅ Remove default spacing from elements - FIXED LIST SPACING
  const style = document.createElement("style");
  style.textContent = `
    /* Paragraph styles */
    p {
      margin: 0px 1px;
      padding: 0px;
    }
    
    /* List styles - MUST MATCH TextElement.jsx listAlignmentStyles */
    ul, ol {
      margin: 0;
      padding: 0;
      padding-left: 1.5em; /* Space for bullet/number marker - matches rendering */
      margin-left: 0;
      list-style-position: outside; /* Markers outside content box - matches rendering */
    }
    
    ul {
      list-style-type: disc;
    }
    
    ol {
      list-style-type: decimal;
    }
    
    li {
      display: list-item;
      margin: 2px 0px;
      padding: 0;
      padding-left: 0;
      margin-left: 0;
      text-indent: 0;
      list-style-position: outside;
    }
    
    /* Ensure wrapped lines align with first line - no extra indentation */
    li p {
      margin: 0;
      padding: 0;
      margin-left: 0;
      padding-left: 0;
      display: inline;
      text-indent: 0;
    }
    
    /* Remove any spacing in list item content */
    li > *:first-child {
      margin-left: 0;
      padding-left: 0;
      text-indent: 0;
    }
    
    /* Handle nested lists */
    li ul,
    li ol {
      margin-top: 0;
      margin-bottom: 0;
      padding-left: 1.5em; /* Same padding for nested lists */
    }
    
    /* Heading styles */
    h1, h2, h3, h4, h5, h6 {
      margin: 0;
      padding: 0;
      font-weight: inherit;
      font-size: inherit;
    }
    
    /* Other common elements */
    div, span, strong, em, i, b {
      margin: 0;
      padding: 0;
    }
    
    /* Block elements */
    blockquote, pre, code {
      margin: 0;
      padding: 0;
    }
    
    /* Table elements */
    table, tr, td, th {
      margin: 0;
      padding: 0;
      border-collapse: collapse;
    }
    
    /* Form elements */
    input, textarea, select, button {
      margin: 0;
      padding: 0;
      font-family: inherit;
      font-size: inherit;
    }
   
  `;

  container.appendChild(style);

  document.body.appendChild(container);
  const height = container.offsetHeight;
  document.body.removeChild(container);

  return height;
}

export function normalizePoints(x1, y1, x2, y2) {
  const rawPoints = [x1, y1, x2, y2];
  const minX = Math.min(x1, x2);
  const minY = Math.min(y1, y2);
  const normalized = rawPoints.map((val, idx) =>
    idx % 2 === 0 ? val - minX : val - minY
  );
  return { minX, minY, points: normalized };
}

export const stripHtmlPreserveFormatting = (html) => {
  if (!html) return "";

  let text = html.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p><p>/gi, "\n\n");
  text = text.replace(/<p>/gi, "");
  text = text.replace(/<\/p>/gi, "\n");

  text = text.replace(/<[^>]*>/g, ""); // Remove all other HTML tags
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&lt;/g, "<");
  text = text.replace(/&gt;/g, ">");

  return text.trim();
};
export const hasTaskContent = (text) => {
  return text.task && !isContentEmpty(text.task);
};
export const hasNoteContent = (text) => {
  return text.note && !isContentEmpty(text.note);
};

export const isContentEmpty = (htmlContent) => {
  if (!htmlContent) return true;
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  const textContent = tempDiv.textContent || tempDiv.innerText || "";
  return !textContent.trim();
};
export const hasUserContent = (text) => {
  return text.content && !isContentEmpty(text.content);
};
export const getTextStyle = (text, styleType) => {
  return text?.textStyles?.[styleType] || {};
};

export const calculateTextBlockWidth = (text) => {
  // If a specific width is provided, use it
  if (text.width) {
    return text.width;
  }

  let maxLineLengthPx = 0;
  const minWidth = 250; // Minimum width for overall readability
  const padding = 10; // Internal padding within the text box

  // Function to estimate character width (can be further refined)
  const getCharWidth = (fontSize = 14, fontFamily = "Roboto") => {
    // These factors are approximations and might need fine-tuning
    // if (fontFamily.includes("Roboto") || fontFamily.includes("Helvetica")) {
    //   return fontSize * 0.55;
    // } else if (fontFamily.includes("Times")) {
    //   return fontSize * 0.5;
    // } else if (
    //   fontFamily.includes("Courier") ||
    //   fontFamily.includes("monospace")
    // ) {
    //   return fontSize * 0.6;
    // }
    return fontSize; // Default heuristic
  };

  // Helper to calculate line width in pixels
  const calculateLineWidth = (content, fontSize, fontFamily) => {
    const words = content.split(/\s+/);
    let currentLinePx = 0;
    let maxWidthPx = 0;
    const spaceWidth = getCharWidth(fontSize, fontFamily); // Estimate for a space

    words.forEach((word) => {
      const wordWidth = word.length * getCharWidth(fontSize, fontFamily);
      if (currentLinePx + wordWidth + spaceWidth > minWidth) {
        // If adding word exceeds minWidth, new line. Consider breaking on word boundaries.
        maxWidthPx = Math.max(maxWidthPx, currentLinePx);
        currentLinePx = wordWidth + spaceWidth;
      } else {
        currentLinePx += wordWidth + spaceWidth;
      }
    });
    maxWidthPx = Math.max(maxWidthPx, currentLinePx);
    return maxWidthPx;
  };

  // Calculate width for Parts section
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (!typeData.parts || typeData.parts.length === 0) continue;

      const typeFontSize = text.textStyles?.type?.fontSize || 16;
      const typeFontFamily = text.textStyles?.type?.fontFamily || "Roboto";
      maxLineLengthPx = Math.max(
        maxLineLengthPx,
        calculateLineWidth(typeName, typeFontSize, typeFontFamily)
      );

      if (typeData.parts && typeData.parts.length > 0) {
        const partFontSize = text.textStyles?.part?.fontSize || 14;
        const partFontFamily = text.textStyles?.part?.fontFamily || "Roboto";

        typeData.parts.forEach(({ part, qty, unit }) => {
          const line = `${part}${qty ? ` - Qty-${qty} ${unit}` : ` ${unit}`}`;
          maxLineLengthPx = Math.max(
            maxLineLengthPx,
            calculateLineWidth(line, partFontSize, partFontFamily)
          );
        });
      }
    }
  }

  // Calculate width for Task content
  if (hasTaskContent(text)) {
    const taskContent = stripHtmlPreserveFormatting(text.task);
    const taskFontSize = getTextStyle(text, "task").fontSize || 14;
    const taskFontFamily = getTextStyle(text, "task").fontFamily || "Roboto";
    maxLineLengthPx = Math.max(
      maxLineLengthPx,
      calculateLineWidth(taskContent, taskFontSize, taskFontFamily)
    );
  }

  // Calculate width for Note content
  if (hasNoteContent(text)) {
    const noteContent = stripHtmlPreserveFormatting(text.note);
    const noteFontSize = getTextStyle(text, "note").fontSize || 14;
    const noteFontFamily = getTextStyle(text, "note").fontFamily || "Roboto";
    maxLineLengthPx = Math.max(
      maxLineLengthPx,
      calculateLineWidth(noteContent, noteFontSize, noteFontFamily)
    );
  }

  // Calculate width for User Content
  if (hasUserContent(text)) {
    const userContent = stripHtmlPreserveFormatting(text.content);
    const contentFontSize = getTextStyle(text, "content").fontSize || 14;
    const contentFontFamily =
      getTextStyle(text, "content").fontFamily || "Roboto";
    maxLineLengthPx = Math.max(
      maxLineLengthPx,
      calculateLineWidth(userContent, contentFontSize, contentFontFamily)
    );
  }

  // Add padding to the maximum calculated content width, ensure it's at least minWidth
  return Math.max(minWidth, maxLineLengthPx + padding * 2); // 2x padding for left/right
};

export const estimateKonvaTextHeight = (
  textContent,
  fontSize = 14,
  fontFamily = "Roboto",
  boxWidth
) => {
  if (!textContent) return 0;
  const dummyText = new Konva.Text({
    text: textContent,
    fontSize: fontSize,
    fontFamily: fontFamily,
    width: boxWidth,
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: fontSize <= 16 ? 1.3 : "2", // Match actual Konva line height
  });
  return dummyText.height();
};

// Helper function to clean HTML content
export const cleanHtmlContent = (htmlContent) => {
  if (!htmlContent) return "";
  return htmlContent
    .replace(/<\/div><div>/g, "<br>")
    .replace(/<div>/g, "")
    .replace(/<\/div>/g, "")
    .replace(/data-text="[^"]*"/g, "") // Remove data-text attributes
    .replace(/<span[^>]*>/g, "") // Remove opening span tags with all attributes
    .replace(/<\/span>/g, "") // Remove closing span tags
    .replace(/<mark[^>]*>/g, "") // Remove opening mark tags with all attributes
    .replace(/<\/mark>/g, ""); // Remove closing mark tags
};

export function calculateGroupHeight(text) {
  let height = 0;

  // Get base font size for font-specific spacing adjustments
  const baseFontSize = text.fontSize || 14;

  // Count total number of part objects to adjust spacing
  let totalPartCount = 0;
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (typeData.parts && typeData.parts.length > 0) {
        totalPartCount += typeData.parts.length;
      }
    }
  }

  // Font-size-specific scaling with part count consideration
  let fontSizeScale;
  if (baseFontSize <= 11) {
    // Small font: adjust spacing based on part count
    if (totalPartCount <= 2) {
      // 1-2 parts: increase spacing slightly (0.8x) for better readability
      fontSizeScale = 0.8;
    } else {
      // Many parts: reduce spacing significantly (0.3x) to prevent exponential growth
      fontSizeScale = 0.3;
    }
  } else if (baseFontSize >= 18) {
    // Big font: increase spacing a bit (1.2x)
    fontSizeScale = 1.2;
  } else {
    // Medium font: normal spacing (1.0x)
    fontSizeScale = 1.0;
  }

  // Dynamic spacing constants - adjusted by font size
  const PADDING_TOP = text.paddingTop || 20;
  const PADDING_BOTTOM = text.paddingBottom || Math.max(1, 2 * fontSizeScale);
  const SECTION_SPACING = text.sectionSpacing || Math.max(1, 2 * fontSizeScale);
  const LABEL_HEIGHT = text.labelHeight || Math.max(3, 6 * fontSizeScale);
  const LINE_SPACING = text.lineSpacing || Math.max(1, 3 * fontSizeScale);
  const CONTENT_PADDING = text.contentPadding || 20;

  const boxWidth = text.width || calculateTextBlockWidth(text);

  height += PADDING_TOP;

  // Parts section
  let hasRenderedContent = false;
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (!typeData.parts || typeData.parts.length === 0) continue;

      // Add section spacing if we already have content
      if (hasRenderedContent) {
        height += SECTION_SPACING;
      }

      // Add type name height if it exists
      if (typeName) {
        const typeFontSize = text.textStyles?.type?.fontSize || 16;
        const typeFontFamily = text.textStyles?.type?.fontFamily || "Roboto";
        height += estimateKonvaTextHeight(
          typeName,
          typeFontSize,
          typeFontFamily,
          boxWidth - CONTENT_PADDING
        );
        height += LINE_SPACING;
      }

      const partFontSize =
        text.textStyles?.part?.fontSize || text.fontSize || 14;
      const partFontFamily = text.textStyles?.part?.fontFamily || "Roboto";

      typeData.parts.forEach((partData, index) => {
        // Skip null or undefined partData entries
        if (!partData) return;
        
        const content = `${partData.part}${
          partData.qty
            ? ` - Qty-${partData.qty} ${partData.unit}`
            : ` ${partData.unit}`
        }`;
        const safeNote = cleanHtmlContent(content);

        const partHeight = estimateKonvaTextHeight(
          safeNote,
          partFontSize,
          partFontFamily,
          boxWidth - 25
        );

        height += partHeight;

        // No line spacing between parts to prevent exponential growth
        // if (index < typeData.parts.length - 1) {
        //   height += LINE_SPACING;
        // }

        // Helper function to calculate note height
        const calculateNoteHeight = (noteContent) => {
          if (!noteContent) return 0;
          const safeNote = cleanHtmlContent(noteContent);
          const noteFontSize =
            text.textStyles?.note?.fontSize || text.fontSize || 14;
          const noteFontFamily = text.textStyles?.note?.fontFamily || "Roboto";

          return measureHtmlHeight(
            safeNote,
            boxWidth - CONTENT_PADDING, // Slight indent for notes
            noteFontSize,
            noteFontFamily
          );
        };

        // Handle itemCodeNote first (system-generated note from Item Code Table)
        if (partData.itemCodeNote) {
          height += calculateNoteHeight(partData.itemCodeNote);
        }

        // Handle user's note (if present)
        if (partData.note) {
          height += calculateNoteHeight(partData.note);
        }
      });

      hasRenderedContent = true;
    }
  }

  // Task section
  if (hasTaskContent(text)) {
    height += LABEL_HEIGHT;

    const safeTask = cleanHtmlContent(text.task);
    const taskFontSize =
      getTextStyle(text, "task").fontSize || text.fontSize || 14;
    const taskFontFamily = getTextStyle(text, "task").fontFamily || "Roboto";
    const taskHeight = measureHtmlHeight(
      safeTask,
      boxWidth - CONTENT_PADDING,
      taskFontSize,
      taskFontFamily
    );

    height += taskHeight + Math.max(1, 3 * fontSizeScale);
    hasRenderedContent = true;
  }

  // Note section
  if (hasNoteContent(text)) {
    // Add section spacing if we already have content
    if (hasRenderedContent) {
      height += SECTION_SPACING;
    }

    height += LABEL_HEIGHT;

    const safeNote = cleanHtmlContent(text.note);
    const noteFontSize =
      getTextStyle(text, "note").fontSize || text.fontSize || 14;
    const noteFontFamily = getTextStyle(text, "note").fontFamily || "Roboto";

    const noteHeight = measureHtmlHeight(
      safeNote,
      boxWidth - CONTENT_PADDING,
      noteFontSize,
      noteFontFamily
    );

    height += noteHeight + Math.max(1, 2 * fontSizeScale);
    hasRenderedContent = true;
  }

  // Content section
  if (hasUserContent(text)) {
    // Add section spacing if we already have content
    if (hasRenderedContent) {
      height += SECTION_SPACING;
    }

    const safeContent = cleanHtmlContent(text.content);
    const contentFontSize =
      getTextStyle(text, "content").fontSize || text.fontSize || 14;
    const contentFontFamily =
      getTextStyle(text, "content").fontFamily || "Roboto";

    const contentHeight = measureHtmlHeight(
      safeContent,
      boxWidth - CONTENT_PADDING,
      contentFontSize,
      contentFontFamily
    );

    height += contentHeight;
    // hasRenderedContent = true;
  }

  // Add bottom padding
  // height += PADDING_BOTTOM;

  // Apply minimum height if specified, otherwise use a reasonable default
  const minGroupHeight = text.minHeight || 80;
  const finalHeight = Math.max(minGroupHeight, height);

  return finalHeight;
}
export function calculateGroupHeight2(text) {
  let height = 0;

  // Get base font size for font-specific spacing adjustments
  const baseFontSize = text.fontSize || 14;

  // Count total number of part objects to adjust spacing
  let totalPartCount = 0;
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (typeData.parts && typeData.parts.length > 0) {
        totalPartCount += typeData.parts.length;
      }
    }
  }

  // Font-size-specific scaling with part count consideration
  let fontSizeScale;
  if (baseFontSize <= 11) {
    // Small font: adjust spacing based on part count
    if (totalPartCount <= 2) {
      // 1-2 parts: increase spacing slightly (0.8x) for better readability
      fontSizeScale = 0.8;
    } else {
      // Many parts: reduce spacing significantly (0.3x) to prevent exponential growth
      fontSizeScale = 0.3;
    }
  } else if (baseFontSize >= 18) {
    // Big font: increase spacing a bit (1.2x)
    fontSizeScale = 1.2;
  } else {
    // Medium font: normal spacing (1.0x)
    fontSizeScale = 1.0;
  }

  // Dynamic spacing constants - adjusted by font size
  const PADDING_TOP = text.paddingTop || 20;
  const PADDING_BOTTOM = text.paddingBottom || Math.max(1, 2 * fontSizeScale);
  const SECTION_SPACING = text.sectionSpacing || Math.max(1, 2 * fontSizeScale);
  const LABEL_HEIGHT = text.labelHeight || Math.max(3, 6 * fontSizeScale);
  const LINE_SPACING = text.lineSpacing || Math.max(1, 3 * fontSizeScale);
  const CONTENT_PADDING = text.contentPadding || 20;

  const boxWidth = text.width || calculateTextBlockWidth(text);

  height += PADDING_TOP;

  // Parts section
  let hasRenderedContent = false;
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (!typeData.parts || typeData.parts.length === 0) continue;

      // Add section spacing if we already have content
      if (hasRenderedContent) {
        height += SECTION_SPACING;
      }

      // Add type name height if it exists
      if (typeName) {
        const typeFontSize = text.textStyles?.type?.fontSize || 16;
        const typeFontFamily = text.textStyles?.type?.fontFamily || "Roboto";
        height += estimateKonvaTextHeight(
          typeName,
          typeFontSize,
          typeFontFamily,
          boxWidth - 10
        );
        height += LINE_SPACING;
      }

      const partFontSize =
        text.textStyles?.part?.fontSize || text.fontSize || 14;
      const partFontFamily = text.textStyles?.part?.fontFamily || "Roboto";

      typeData.parts.forEach((partData, index) => {
        // Skip null or undefined partData entries
        if (!partData) return;
        
        const content = `${partData.part}${
          partData.qty
            ? ` - Qty-${partData.qty} ${partData.unit}`
            : ` ${partData.unit}`
        }`;
        const safeNote = cleanHtmlContent(content);

        const partHeight = estimateKonvaTextHeight(
          safeNote,
          partFontSize,
          partFontFamily,
          boxWidth - 20
        );
        height += partHeight;

        // No line spacing between parts to prevent exponential growth
        // if (index < typeData.parts.length - 1) {
        //   height += LINE_SPACING;
        // }

        // Handle part notes
        if (partData.note) {
          const safeNote = cleanHtmlContent(partData.note);
          const noteFontSize =
            text.textStyles?.note?.fontSize || text.fontSize || 14;
          const noteFontFamily = text.textStyles?.note?.fontFamily || "Roboto";

          const noteHeight = measureHtmlHeight(
            safeNote,
            boxWidth - 10, // Slight indent for notes
            noteFontSize,
            noteFontFamily
          );

          height += noteHeight; // No extra spacing for part notes
        }
      });

      hasRenderedContent = true;
    }
  }

  // Task section
  if (hasTaskContent(text)) {
    height += LABEL_HEIGHT;

    const safeTask = cleanHtmlContent(text.task);
    const taskFontSize =
      getTextStyle(text, "task").fontSize || text.fontSize || 14;
    const taskFontFamily = getTextStyle(text, "task").fontFamily || "Roboto";
    const taskHeight = measureHtmlHeight(
      safeTask,
      boxWidth,
      taskFontSize,
      taskFontFamily
    );

    height += taskHeight;
    hasRenderedContent = true;
  }

  // Note section
  if (hasNoteContent(text)) {
    // Add section spacing if we already have content
    if (hasRenderedContent) {
      height += SECTION_SPACING;
    }

    height += LABEL_HEIGHT;

    const safeNote = cleanHtmlContent(text.note);
    const noteFontSize =
      getTextStyle(text, "note").fontSize || text.fontSize || 14;
    const noteFontFamily = getTextStyle(text, "note").fontFamily || "Roboto";

    const noteHeight = measureHtmlHeight(
      safeNote,
      boxWidth - CONTENT_PADDING,
      noteFontSize,
      noteFontFamily
    );

    height += noteHeight;
    hasRenderedContent = true;
  }

  // Content section
  if (hasUserContent(text)) {
    // Add section spacing if we already have content
    if (hasRenderedContent) {
      height += SECTION_SPACING;
    }

    const safeContent = cleanHtmlContent(text.content);
    const contentFontSize =
      getTextStyle(text, "content").fontSize || text.fontSize || 14;
    const contentFontFamily =
      getTextStyle(text, "content").fontFamily || "Roboto";

    const contentHeight = measureHtmlHeight(
      safeContent,
      boxWidth - 10,
      contentFontSize,
      contentFontFamily
    );

    height += contentHeight + Math.max(1, 3 * fontSizeScale);
    hasRenderedContent = true;
  }

  // Add bottom padding
  height += PADDING_BOTTOM;

  // Apply minimum height if specified, otherwise use a reasonable default
  const minGroupHeight = text.minHeight || 80;
  const finalHeight = Math.max(minGroupHeight, height);

  return finalHeight;
}

export function calculateGroupHeight3(text) {
  let height = 0;

  // Dynamic spacing constants - fixed values to prevent excessive spacing
  const PADDING_TOP = text.paddingTop || 20;
  const PADDING_BOTTOM = text.paddingBottom || 3;
  const SECTION_SPACING = text.sectionSpacing || 6;
  const LABEL_HEIGHT = text.labelHeight || 10;
  const LINE_SPACING = text.lineSpacing || 3;
  const CONTENT_PADDING = text.contentPadding || 20;

  const boxWidth = text.width || calculateTextBlockWidth(text);

  height += PADDING_TOP;

  // Parts section
  let hasRenderedContent = false;
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (!typeData.parts || typeData.parts.length === 0) continue;

      // Add section spacing if we already have content
      if (hasRenderedContent) {
        height += SECTION_SPACING;
      }

      // Add type name height if it exists
      if (typeName) {
        const typeFontSize = text.textStyles?.type?.fontSize || 16;
        const typeFontFamily = text.textStyles?.type?.fontFamily || "Roboto";
        height += estimateKonvaTextHeight(
          typeName,
          typeFontSize,
          typeFontFamily,
          boxWidth - CONTENT_PADDING
        );
        height += LINE_SPACING;
      }

      const partFontSize =
        text.textStyles?.part?.fontSize || text.fontSize || 14;
      const partFontFamily = text.textStyles?.part?.fontFamily || "Roboto";

      typeData.parts.forEach((partData, index) => {
        // Skip null or undefined partData entries
        if (!partData) return;
        
        const content = `${partData.part}${
          partData.qty
            ? ` - Qty-${partData.qty} ${partData.unit}`
            : ` ${partData.unit}`
        }`;
        const safeNote = cleanHtmlContent(content);

        const partHeight = estimateKonvaTextHeight(
          safeNote,
          partFontSize,
          partFontFamily,
          boxWidth - 25
        );

        height += partHeight;

        // Add line spacing between parts (but not after the last one)
        if (index < typeData.parts.length - 1) {
          height += LINE_SPACING;
        }

        // Handle part notes
        if (partData.note) {
          const safeNote = cleanHtmlContent(partData.note);
          const noteFontSize =
            text.textStyles?.note?.fontSize || text.fontSize || 14;
          const noteFontFamily = text.textStyles?.note?.fontFamily || "Roboto";

          const noteHeight = measureHtmlHeight(
            safeNote,
            boxWidth - CONTENT_PADDING, // Slight indent for notes
            noteFontSize,
            noteFontFamily
          );

          height += noteHeight + LINE_SPACING;
        }
      });

      hasRenderedContent = true;
    }
  }

  // Task section
  if (hasTaskContent(text)) {
    height += LABEL_HEIGHT;

    const safeTask = cleanHtmlContent(text.task);
    const taskFontSize =
      getTextStyle(text, "task").fontSize || text.fontSize || 14;
    const taskFontFamily = getTextStyle(text, "task").fontFamily || "Roboto";
    const taskHeight = measureHtmlHeight(
      safeTask,
      boxWidth - CONTENT_PADDING,
      taskFontSize,
      taskFontFamily
    );

    height += taskHeight + 3;
    hasRenderedContent = true;
  }

  // Note section
  if (hasNoteContent(text)) {
    // Add section spacing if we already have content
    if (hasRenderedContent) {
      height += SECTION_SPACING;
    }

    height += LABEL_HEIGHT;

    const safeNote = cleanHtmlContent(text.note);
    const noteFontSize =
      getTextStyle(text, "note").fontSize || text.fontSize || 14;
    const noteFontFamily = getTextStyle(text, "note").fontFamily || "Roboto";

    const noteHeight = measureHtmlHeight(
      safeNote,
      boxWidth - CONTENT_PADDING,
      noteFontSize,
      noteFontFamily
    );

    height += noteHeight + 2;
    hasRenderedContent = true;
  }

  // Content section
  if (hasUserContent(text)) {
    // Add section spacing if we already have content
    if (hasRenderedContent) {
      height += SECTION_SPACING;
    }

    const safeContent = cleanHtmlContent(text.content);
    const contentFontSize =
      getTextStyle(text, "content").fontSize || text.fontSize || 14;
    const contentFontFamily =
      getTextStyle(text, "content").fontFamily || "Roboto";

    const contentHeight = measureHtmlHeight(
      safeContent,
      boxWidth - CONTENT_PADDING,
      contentFontSize,
      contentFontFamily
    );

    height += contentHeight;
    // hasRenderedContent = true;
  }

  // Add bottom padding
  // height += PADDING_BOTTOM;

  // Apply minimum height if specified, otherwise use a reasonable default
  const minGroupHeight = text.minHeight || 80;
  const finalHeight = Math.max(minGroupHeight, height);

  return finalHeight;
}

export function calculateLabeHeight(
  html,
  width,
  fontSize,
  lineHeight = "normal",
  fontFamily
) {
  const container = document.createElement("div");

  // ✅ Remove only positioning styles that affect height calculation
  const cleanedHtml = html.replace(/style="[^"]*"/g, (match) => {
    return match
      .replace(/position:\s*[^;]+;?/gi, "")
      .replace(/transform:[^;]+;?/gi, "")
      .replace(/top:\s*[^;]+;?/gi, "")
      .replace(/left:\s*[^;]+;?/gi, "")
      .replace(/z-index:\s*[^;]+;?/gi, "")
      .replace(/style=""/g, "");
  });

  container.innerHTML = cleanedHtml;

  container.style.position = "absolute";
  container.style.visibility = "hidden";
  container.style.pointerEvents = "none";
  container.style.width = `${width - 20}px`;
  container.style.fontSize = `${fontSize}px`;
  container.style.fontFamily = fontFamily;
  container.style.fontWeight = "bold";

  container.style.lineHeight = fontSize <= 16 ? 1.3 : "2";
  container.style.whiteSpace = "pre-wrap";
  container.style.wordBreak = "break-word";

  // ✅ Remove default spacing from elements - FIXED LIST SPACING
  const style = document.createElement("style");
  style.textContent = `
    /* Paragraph styles */
    p {
      margin: 0px 1px;
      padding: 0px;
    }
    
    /* List styles - REMOVED EXTRA MARGINS */
    ul, ol {
      margin: 0;
      padding: 0;
      list-style-position: inside;
      /* Removed margin-block-start and margin-block-end */
    }
    
    li {
      display: list-item;
      margin: 2px 0px;
      padding: 0;
      line-height: 1.4;
    }
    
    /* Heading styles */
    h1, h2, h3, h4, h5, h6 {
      margin: 0;
      padding: 0;
      font-weight: inherit;
      font-size: inherit;
    }
    
    /* Other common elements */
    div, span, strong, em, i, b {
      margin: 0;
      padding: 0;
    }
    
    /* Block elements */
    blockquote, pre, code {
      margin: 0;
      padding: 0;
    }
    
    /* Table elements */
    table, tr, td, th {
      margin: 0;
      padding: 0;
      border-collapse: collapse;
    }
    
    /* Form elements */
    input, textarea, select, button {
      margin: 0;
      padding: 0;
      font-family: inherit;
      font-size: inherit;
    }
    
    /* Line breaks */
    br {
      line-height: inherit;
    }
  `;

  container.appendChild(style);

  document.body.appendChild(container);
  const height = container.offsetHeight;
  document.body.removeChild(container);

  return height;
}

export const formatListContent = (content, type) => {
  if (!type || !content) return content;

  const lines = content.split("\n");
  return lines
    .map((line, index) => {
      if (line.trim() === "") return ""; // Handle empty lines
      if (type === "bullet") return `• ${line}`;
      if (type === "number") return `${index + 1}. ${line}`;
      return line;
    })
    .filter(Boolean) // Remove empty strings
    .join("\n");
};

export const getPages = (canvasData) => {
  if (!canvasData || !canvasData.pages) {
    return [];
  }
  return canvasData.pages;
};

/**
 * Converts a blob URL to a base64 data URL
 * @param {string} blobUrl - The blob URL to convert
 * @returns {Promise<string|null>} - The base64 data URL or null if conversion fails
 */
export const convertBlobToBase64 = async (blobUrl) => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
};

/**
 * Safely loads an image from various sources (blob URL, base64, regular URL)
 * @param {string} imageSrc - The image source to load
 * @returns {Promise<HTMLImageElement|null>} - The loaded image element or null if failed
 */
export const loadImageSafely = async (imageSrc) => {
  return new Promise((resolve) => {
    const img = new window.Image();

    const handleError = async () => {
      // Only try the base64 fallback for blob URLs
      if (imageSrc && imageSrc.startsWith("blob:")) {
        const base64Data = await convertBlobToBase64(imageSrc);
        if (base64Data) {
          // Try loading from base64
          const img2 = new window.Image();
          img2.onload = () => resolve(img2);
          img2.onerror = () => resolve(null);
          img2.src = base64Data;
          return;
        }
      }
      resolve(null);
    };

    img.onload = () => resolve(img);
    img.onerror = handleError;
    img.src = imageSrc;
  });
};

/**
 * Test function to verify blob URL conversion works
 * This can be called from browser console for debugging
 */
export const testBlobConversion = async (blobUrl) => {
  try {
    const base64Data = await convertBlobToBase64(blobUrl);
    if (base64Data) {
      return base64Data;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const generateESTDateTimeString = (dateInput = new Date()) => {
  // Get app setup state from Redux store
  let appSetupState = null;
  if (typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetupState = state.appSetup;
  }

  const date = new Date(dateInput);
  return formatDateToAppTimezoneAlt(date.toISOString(), appSetupState);
};

export const renderPreview = (attachment, label) => {

  if (!attachment || !attachment.attachment) {
    
    return null;
  }

  const url = attachment.attachment;

  // Derive mime type from explicit type or data URL
  const explicitType = attachment.type || "";
  let dataUrlMime = "";
  if (typeof url === "string" && url.startsWith("data:")) {
    // data:[<mime type>][;base64],<data>
    const afterDataPrefix = url.slice(5);
    dataUrlMime = afterDataPrefix.split(";")[0] || "";
  }
  const mime = (explicitType || dataUrlMime).toLowerCase();

  // Fallback: extension-based detection only if no mime available and not a data/blob URL
  const extension =
    mime ||
    (typeof url === "string" &&
      !url.startsWith("data:") &&
      !url.startsWith("blob:"))
      ? (url.split(".").pop() || "").toLowerCase()
      : "";

  // Helper function to get the full URL
  const getFullUrl = (url) => {

    // If it's already a full URL (starts with http/https), use it as is
    if (
      typeof url === "string" &&
      (url.startsWith("http://") || url.startsWith("https://"))
    ) {
      return url;
    }
    // If it's a data URL or blob URL, return as-is
    if (
      typeof url === "string" &&
      (url.startsWith("data:") || url.startsWith("blob:"))
    ) {
      return url;
    }
    // Otherwise, prepend the base URL for relative paths
    const baseUrl = BASE_URL.replace("/api/", "");
    const fullUrl = baseUrl + url;
    return fullUrl;
  };

  if (
    mime.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "webp"].includes(extension)
  ) {
    return (
      <img
        src={getFullUrl(url)}
        alt={label}
        crossOrigin="anonymous"
        style={{
          maxWidth: "200px",
          marginTop: "20px",
        }}
      />
    );
  } else if (
    mime.startsWith("video/") ||
    ["mp4", "webm", "ogg"].includes(extension)
  ) {
    return (
      <video
        controls
        src={getFullUrl(url)}
        style={{
          maxWidth: "300px",
          marginTop: "20px",
        }}
      />
    );
  } else if (mime === "application/pdf" || ["pdf"].includes(extension)) {
    return (
      <a
        href={getFullUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: "20px",
          display: "inline-block",
        }}
      >
        📄 View PDF
      </a>
    );
  } else {
    return (
      <a
        href={getFullUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: "20px",
          display: "inline-block",
        }}
      >
        📎 View Attachment
      </a>
    );
  }
};

export const renderVideo = (video) => {

  if (!video || (!video.video && !video.video_url)) {
    return null;
  }

  // Video may come as { video: { url: ... } } or { video_url: '...' }
  const source = video.video || video.video_url;
  const url = getVideoUrl(source);

  return (
    <video
      className="video-player"
      src={url}
      controls
      style={{
        width: "100%",
        maxHeight: "360px",
        borderRadius: "6px",
        backgroundColor: "#000",
      }}
      onError={(e) => console.error("Preview video load error:", e, url)}
    />
  );
};
